import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import type { ITeamRoutingQuestion, TeamRoutingAnswers } from '../types';

/** One answer given so far, beside the question it answered — kept so a caller can say what was
 *  answered in the question's own words, and take the last one back. */
export interface IRouteAnswer {
  question: ITeamRoutingQuestion;
  value: string;
}

/** The wire's answers object — every answer so far, keyed by question code. Both routes that
 *  take answers keep none between calls, so each request carries all of them. */
export function routingAnswers(given: readonly IRouteAnswer[]): TeamRoutingAnswers {
  return Object.fromEntries(given.map(({ question, value }) => [question.code, value]));
}

/** The answers given so far, in order, each in its question's words — above the question they
 *  led to, on both screens that ask them. */
export function GivenAnswers({ given }: { given: readonly IRouteAnswer[] }) {
  if (given.length === 0) return null;
  return (
    <ol className="mb-6 grid gap-1 text-xs text-muted-foreground">
      {given.map((answer) => (
        <li key={answer.question.code}>
          {answer.question.text} — <strong>{answerLabel(answer)}</strong>
        </li>
      ))}
    </ol>
  );
}

/** An answer in the question's own words: its option's label, else the value as sent. */
function answerLabel({ question, value }: IRouteAnswer): string {
  return question.options.find((option) => option.value === value)?.label ?? value;
}

/**
 * One gating question, as survey-api serves it — `{code, kind, text, options}` — and nothing
 * else: which question comes next, which answers a question takes and where an answer leads are
 * all the gating tree's (`TeamGating.cs`), on the server, and this only draws what it was handed.
 *
 * **One rendering for both callers** (the plan's R4): a new team's route step on
 * `/registration`, and a team already on file answering its own routing. The two differ only
 * in the shape their refusal comes back in — registration's `errors['answers.<code>']`, the
 * routing route's `problems[]` — so each caller maps its own onto `messages` and nothing else
 * about the question differs.
 *
 * **Every question is a radio group of the server's options**, sent as the option's `value`
 * exactly as spelled. SR-416's tree asks only `choice` questions (`private_wealth`,
 * `trust_company`, `ria_submission`, `structure`); the percent question it retired is not drawn
 * as one, and a question of a kind this build does not know — an API from before SR-416 — is
 * drawn as a choice over whatever options it sends (block 1's B1-4). Continue waits for an
 * answer, since without one there is nothing to send.
 */
export function TeamRoutingQuestion({
  question,
  initial,
  messages,
  pending,
  onAnswer
}: {
  question: ITeamRoutingQuestion;
  /** The answer to start from — the one "Change my last answer" took back. */
  initial?: string;
  /** survey-api's refusal of this question's answer, as the caller's refusal shape carries
   *  it. */
  messages?: string[];
  pending: boolean;
  onAnswer: (answer: string) => void;
}) {
  const [draft, setDraft] = React.useState(initial ?? '');
  const id = `team-routing-${question.code}`;
  const refused = !!messages?.length;

  return (
    <form
      noValidate
      className="grid gap-5"
      onSubmit={(event) => {
        event.preventDefault();
        if (draft) onAnswer(draft);
      }}
    >
      <fieldset className="grid gap-2" aria-invalid={refused}>
        <legend className="mb-2 text-base text-[var(--color-royal-blue)]">
          {question.text}
        </legend>
        {question.options.map((option) => (
          <label key={option.value} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name={id}
              value={option.value}
              checked={draft === option.value}
              onChange={() => setDraft(option.value)}
              className="size-4 accent-[var(--color-sapphire)]"
            />
            {option.label}
          </label>
        ))}
        {messages?.map((message) => (
          <p key={message} className="text-xs text-destructive">
            {message}
          </p>
        ))}
      </fieldset>

      <Button type="submit" disabled={pending || !draft} className="w-full">
        {pending ? 'Checking…' : 'Continue'}
      </Button>
    </form>
  );
}

/**
 * A refusal of one answer, as a sentence. survey-api words it as a predicate on the answer's
 * key — `answers.structure` "is not asked on the way these answers take", or "is not an answer
 * this question takes" — so beside the question it is said of "that answer", in the server's
 * words. A message that is already a sentence is left as sent.
 */
export function answerRefusal(message: string): string {
  return /^[a-z]/.test(message) ? `That answer ${message.replace(/\.$/, '')}.` : message;
}
